import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import Locals from '../providers/Locals';
import { captureApiError } from '@odo/shared-ui/observability/captureApiError';

const API_BASE_URL = Locals.config().base_api_url;

/** localStorage key for the WordPress-issued bearer token. Exported so callers
 *  that can't go through this module's axios instances (e.g. a native `fetch`
 *  with `keepalive` for teardown-safe writes, which the interceptor below
 *  can't cover) still read the same key rather than duplicating the string. */
export const AUTH_TOKEN_STORAGE_KEY = 'odo_login_token';

const api = axios.create({
    baseURL: API_BASE_URL + '/api/v1',
    headers: {
        'Content-Type': 'application/json'
    }
});

const files_api = axios.create({
    baseURL: API_BASE_URL + '/api/v1'
});

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
};

const requestErrorInterceptor = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'Request failed';
    return Promise.reject(new Error(message));
};

const responseInterceptor = (response: AxiosResponse) => response;

const responseErrorInterceptor = (error: unknown) => {
    const axiosError = error as { response?: { status?: number } };
    const status = axiosError.response?.status;

    if (status === 401 && window.location.pathname !== '/login') {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        const from = window.location.pathname + window.location.search + window.location.hash;
        const suffix = from && from !== '/' ? `?from=${encodeURIComponent(from)}` : '';
        window.location.href = `/login${suffix}`;
    } else if (status === 403 && globalThis.location.pathname !== '/forbidden') {
        captureApiError(error);
        globalThis.location.href = '/forbidden';
    } else {
        captureApiError(error);
    }

    return Promise.reject(error);
};

api.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

files_api.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
files_api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export default api;
export {
    files_api,
    requestInterceptor,
    requestErrorInterceptor,
    responseInterceptor,
    responseErrorInterceptor
};
